import { useCallback, useEffect, useRef, useState } from "react";
import { User } from "lucide-react";

export interface VideoSlide {
  src: string;
  label: string;
  caption: string;
}

interface VideoCarouselProps {
  title: string;
  icon?: React.ReactNode;
  slides: VideoSlide[];
  /** border-right divider between this panel and the next */
  divider?: boolean;
}

export default function VideoCarousel({
  title,
  icon,
  slides,
  divider = true,
}: VideoCarouselProps) {
  const [current, setCurrent] = useState(0);
  const [progress, setProgress] = useState(0);
  const [captionVisible, setCaptionVisible] = useState(false);
  const videoRefs = useRef<(HTMLVideoElement | null)[]>([]);
  const progressRafRef = useRef<number | undefined>(undefined);
  const captionTimerRef = useRef<number | undefined>(undefined);

  const stopProgress = useCallback(() => {
    if (progressRafRef.current !== undefined) {
      cancelAnimationFrame(progressRafRef.current);
      progressRafRef.current = undefined;
    }
  }, []);

  const playSlide = useCallback(
    (idx: number) => {
      stopProgress();
      setCurrent(idx);
      setProgress(0);
      setCaptionVisible(false);

      // hide all, show current
      videoRefs.current.forEach((v, i) => {
        if (!v) return;
        if (i !== idx) {
          v.pause();
          v.currentTime = 0;
        }
      });

      const vid = videoRefs.current[idx];
      if (!vid) return;

      vid.play().catch(() => {});

      // caption fade-in after brief delay
      if (captionTimerRef.current !== undefined) clearTimeout(captionTimerRef.current);
      captionTimerRef.current = window.setTimeout(() => setCaptionVisible(true), 100);

      // progress bar via rAF
      const tick = () => {
        if (!vid.duration) {
          progressRafRef.current = requestAnimationFrame(tick);
          return;
        }
        const p = Math.min(vid.currentTime / vid.duration, 1);
        setProgress(p);
        if (p < 1) {
          progressRafRef.current = requestAnimationFrame(tick);
        }
      };
      progressRafRef.current = requestAnimationFrame(tick);
    },
    [stopProgress]
  );

  // auto-advance on video end
  useEffect(() => {
    const handlers: (() => void)[] = [];
    videoRefs.current.forEach((v, i) => {
      if (!v) return;
      const handler = () => {
        const next = (i + 1) % slides.length;
        playSlide(next);
      };
      v.addEventListener("ended", handler);
      handlers.push(() => v.removeEventListener("ended", handler));
    });
    return () => handlers.forEach((h) => h());
  }, [slides.length, playSlide]);

  // start playing first slide on mount
  useEffect(() => {
    playSlide(0);
    return () => {
      stopProgress();
      if (captionTimerRef.current !== undefined) clearTimeout(captionTimerRef.current);
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div
      className="hidden md:flex flex-col min-w-0"
      style={{
        flex: 3,
        alignSelf: "stretch",
        borderRight: divider ? "1px solid #e2e8f0" : undefined,
      }}
    >
      {/* Panel header */}
      <div style={{ padding: "16px 16px 12px 0", flexShrink: 0 }}>
        <h2
          style={{
            fontSize: 14,
            fontWeight: 600,
            color: "#334155",
            letterSpacing: "0.02em",
            display: "flex",
            alignItems: "center",
            gap: 8,
          }}
        >
          {icon ?? (
            <User size={15} className="text-slate-500" />
          )}
          {title}
        </h2>
      </div>

      {/* Video wrap */}
      <div className="relative w-full rounded-2xl overflow-hidden bg-white border border-slate-200 shadow-[0_20px_25px_-5px_rgba(0,0,0,.1),0_8px_10px_-6px_rgba(0,0,0,.1)]" style={{ height: 400 }}>
        {/* Label badge */}
        <div className="absolute top-3 left-3 right-3 z-10 flex items-center gap-1.5">
          <span
            className="text-[11px] font-medium tracking-[0.05em] uppercase px-2.5 py-[3px] rounded-full border border-slate-200"
            style={{ background: "rgba(241,245,249,0.85)", backdropFilter: "blur(6px)", color: "#334155" }}
          >
            {slides[current]?.label}
          </span>
        </div>

        {/* Videos */}
        {slides.map((slide, i) => (
          <div
            key={slide.src}
            className="absolute inset-0 transition-opacity duration-[600ms] ease-in-out"
            style={{ opacity: i === current ? 1 : 0 }}
          >
            <video
              ref={(el) => { videoRefs.current[i] = el; }}
              src={slide.src}
              muted
              playsInline
              preload="metadata"
              className="w-full h-full object-contain bg-white"
            />
          </div>
        ))}

        {/* Dot navigation */}
        <div className="absolute bottom-3.5 left-1/2 -translate-x-1/2 z-10 flex gap-1.5">
          {slides.map((_, i) => (
            <button
              key={i}
              onClick={() => playSlide(i)}
              className="rounded-full transition-all duration-300"
              style={{
                width: i === current ? 18 : 6,
                height: 6,
                background: i === current ? "rgba(59,130,246,0.9)" : "rgba(0,0,0,0.2)",
              }}
            />
          ))}
        </div>

        {/* Progress bar */}
        <div
          className="absolute bottom-0 left-0 h-[3px] z-10"
          style={{
            width: `${progress * 100}%`,
            background: "linear-gradient(to right, #06b6d4, #3b82f6)",
            transformOrigin: "left",
          }}
        />
      </div>

      {/* Caption */}
      <div
        className="mt-2.5 rounded-xl border border-slate-200 bg-slate-50 overflow-hidden transition-all duration-[1500ms] ease-[cubic-bezier(0.16,1,0.3,1)]"
        style={{ maxHeight: captionVisible ? 120 : 0, opacity: captionVisible ? 1 : 0, flexShrink: 0 }}
      >
        <p className="px-3.5 py-2.5 text-[12px] text-slate-500 leading-relaxed">
          {slides[current]?.caption}
        </p>
      </div>
    </div>
  );
}
